// Collapsible sidebar sections. The sidebar markup in index.html is a
// flat <nav> of alternating section-header <div>s and item <a>s:
//
//   <div class="sidebar-label"><p>MAIN</p></div>
//   <a>Home</a>
//   <div class="sidebar-label"><p>CRM</p></div>
//   <a>Accounts</a>
//   <a>Contacts</a>
//   <div class="sidebar-label"><p>WORK</p></div>
//   ...
//
// This module groups items by their preceding header, toggles them on
// click of the header, and persists collapsed state per-section in
// localStorage so a refresh restores the same view.
//
// We don't touch the index.html markup — handlers are attached at boot.

const STORAGE_KEY = 'pm_sidebar_sections_collapsed';

function loadState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
}

function saveState(set) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify([...set])); } catch { /* quota */ }
}

// A "section header" is a sidebar-label DIV that contains a <p> with the
// uppercase section title. Item labels are <span>s inside <a>s — different
// shape — so this lets us reliably tell them apart.
function isSectionHeader(el) {
  return el.tagName === 'DIV' && el.classList.contains('sidebar-label') && el.querySelector(':scope > p');
}

function sectionKey(headerEl) {
  return (headerEl.querySelector('p')?.textContent || '').trim().toLowerCase();
}

export function initSidebarSections() {
  const nav = document.getElementById('sidebar-nav');
  if (!nav) return;

  // Group: header → array of <a> items that belong to it.
  const groups = [];
  let current = null;
  for (const child of Array.from(nav.children)) {
    if (isSectionHeader(child)) {
      current = { header: child, items: [] };
      groups.push(current);
    } else if (current && child.tagName === 'A') {
      current.items.push(child);
    }
  }
  if (groups.length === 0) return;

  const collapsed = loadState();

  // Inject a chevron, make the header role="button", and wire toggle.
  for (const group of groups) {
    const key = sectionKey(group.header);
    if (!key) continue;

    // Decorate the header. Position chevron inline with the label text.
    group.header.style.cursor = 'pointer';
    group.header.setAttribute('role', 'button');
    group.header.setAttribute('tabindex', '0');
    group.header.setAttribute('aria-expanded', collapsed.has(key) ? 'false' : 'true');

    const p = group.header.querySelector('p');
    if (p && !p.querySelector('.section-chevron')) {
      // Convert "MAIN" → "MAIN ▸". Chevron is a span we can rotate.
      const chev = document.createElement('span');
      chev.className = 'section-chevron ml-1 inline-block transition-transform';
      chev.style.fontSize = '8px';
      chev.style.lineHeight = '1';
      chev.textContent = '▾';
      chev.setAttribute('aria-hidden', 'true');
      p.appendChild(chev);
    }

    const applyState = () => {
      const isCollapsed = collapsed.has(key);
      group.header.setAttribute('aria-expanded', isCollapsed ? 'false' : 'true');
      const chev = group.header.querySelector('.section-chevron');
      if (chev) chev.style.transform = isCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)';
      for (const a of group.items) {
        a.classList.toggle('hidden', isCollapsed);
      }
    };
    applyState();

    const toggle = () => {
      if (collapsed.has(key)) collapsed.delete(key);
      else collapsed.add(key);
      saveState(collapsed);
      applyState();
    };

    group.header.addEventListener('click', toggle);
    group.header.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggle();
      }
    });
  }
}
